import { IonApp, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Route, Switch } from "react-router-dom";
import { useEffect, useState } from "react";
import AppTabs from "./AppTabs";
import NotFoundPage from "./pages/NotFoundPage";
import UserIdentity from "./pages/UserIdentity";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";
import {
  setTheme,
  themeDetectionHandler,
  ThemeContext,
} from "./util/ThemeManager";
import { hideSplashScreen } from "./util/SplashScreen";
import AuthCallback from "./pages/AuthCallback";
import { getThemePreference } from "./util/Preferences";
import { ThemePreference } from "./models/common/ThemePreference";
import { AuthContext } from "./util/OAuth";
import PortalAuthCallback from "./pages/PortalAuthCallback";
import { getScreenOrientation } from "./util/Device";
import {
  TranslationsContext,
  TranslationMessagesContext,
} from "./util/Translations";
import AppUrlListener from "./pages/AppUrlListener";
import QRCodeService from "./pages/QRCodeService";
import { SessionProvider } from "./util/SessionContext";
import SessionKilledListener from "./components/SessionKilledListener";
import AppInsightsRouteTracker from "./components/AppInsightsRouteTracker";
import { Keyboard } from "@capacitor/keyboard";

//do not remove this, these needs to be loaded eagerly for camera to work in offline
import "@capacitor/camera/dist/esm/web";
import "@ionic/pwa-elements/dist/index.mjs";
import "@ionic/pwa-elements/dist/esm-es5/loader.mjs";
import "@ionic/pwa-elements/dist/esm-es5/pwa-action-sheet.entry.js";
import "@ionic/pwa-elements/dist/esm-es5/pwa-camera.entry.js";
import "@ionic/pwa-elements/dist/esm-es5/pwa-camera-modal.entry.js";
import "@ionic/pwa-elements/dist/esm-es5/pwa-camera-modal-instance.entry.js";

setupIonicReact();

const App: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [themePreference, setThemePreference] = useState<ThemePreference>();
  const [translations, setTranslations] = useState<any>({});
  const [translatedMessages, setTranslatedMessages] = useState<any>({});

  useEffect(() => {
    getScreenOrientation().then(() => {
      hideSplashScreen({ fadeOutDuration: 1000 });
    });
  }, []);

  useEffect(() => {
    //WKWebView on iOS sometimes fails to repaint/resize after the keyboard closes.
    const hideHandler = Keyboard.addListener("keyboardDidHide", () => {
      console.log("keyboardDidHide: listener got trigger.");

      // Trigger a tiny scroll to force webview repaint
      window.scrollTo(0, 0);
      document.body.scrollIntoView();
    });

    return () => {
      hideHandler.remove();
    };
  }, []);

  useEffect(() => {
    getThemePreference().then((storedThemePreference) => {
      setThemePreference(storedThemePreference as ThemePreference);
    });
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
    if (prefersDark?.addEventListener) {
      prefersDark.addEventListener("change", (event) => {
        themeDetectionHandler(event, themePreference);
      });
    } else {
      prefersDark.addListener((event) => {
        themeDetectionHandler(event, themePreference);
      });
    }
  }, []);

  useEffect(() => {
    themePreference && setTheme(themePreference);
  }, [themePreference]);

  return (
    <IonApp class="scanner-hide">
      <SessionProvider>
        <AuthContext.Provider value={{ loggedIn, setLoggedIn }}>
          <ThemeContext.Provider
            value={{ themePreference, setThemePreference }}
          >
            <TranslationsContext.Provider
              value={{ translations, setTranslations }}
            >
              <TranslationMessagesContext.Provider
                value={{ translatedMessages, setTranslatedMessages }}
              >
                <IonReactRouter>
                  <AppInsightsRouteTracker />
                  <Switch>
                    <Route path="/" exact={true}>
                      <UserIdentity />
                    </Route>
                    <Route path="/AuthCallback">
                      <AuthCallback />
                    </Route>
                    <Route path="/PortalAuthCallback">
                      <PortalAuthCallback />
                    </Route>
                    <Route path="/qrcodesvc">
                      <QRCodeService />
                    </Route>
                    <Route path="/UserIdentity">
                      <UserIdentity />
                    </Route>
                    <AppTabs />
                    <Route>
                      <NotFoundPage />
                    </Route>
                  </Switch>
                  <AppUrlListener />
                </IonReactRouter>
                <SessionKilledListener />
              </TranslationMessagesContext.Provider>
            </TranslationsContext.Provider>
          </ThemeContext.Provider>
        </AuthContext.Provider>
      </SessionProvider>
    </IonApp>
  );
};

export default App;
